import { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { 
  getAIDocuments, 
  uploadAIDocument, 
  deleteAIDocument,
  getAIConsultantVideos,
  createAIConsultantVideo,
  deleteAIConsultantVideo,
  reindexAIConsultant,
  getIndexedDatasets,
  deleteIndexedDataset,
  trainAIConsultant,
  getTrainingHistory,
  getVerticals,
  testChatMemory
} from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { 
  FileText, 
  Upload, 
  Trash2, 
  Brain,
  Loader2,
  CheckCircle,
  Info,
  Video,
  Plus,
  X,
  Play,
  RefreshCw,
  Database,
  Zap,
  AlertCircle,
  Building2,
  CheckCircle2,
  XCircle,
  History,
  MessageSquare
} from 'lucide-react';

export default function AdminKnowledgeBase() {
  const [documents, setDocuments] = useState([]);
  const [videos, setVideos] = useState([]);
  const [indexedDatasets, setIndexedDatasets] = useState([]);
  const [trainingHistory, setTrainingHistory] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isReindexing, setIsReindexing] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const [isTestingMemory, setIsTestingMemory] = useState(false);
  const [memoryTestResults, setMemoryTestResults] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoForm, setVideoForm] = useState({ title: '', description: '', youtube_url: '', category: 'general' });
  const [selectedVerticalFilter, setSelectedVerticalFilter] = useState('');
  const [trainingResults, setTrainingResults] = useState(null);
  const fileInputRef = useRef(null);

  const MAX_DOCUMENTS = 21;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [docsRes, videosRes, datasetsRes, historyRes, verticalsRes] = await Promise.all([
        getAIDocuments(),
        getAIConsultantVideos(),
        getIndexedDatasets(),
        getTrainingHistory(),
        getVerticals()
      ]);
      setDocuments(docsRes.data);
      setVideos(videosRes.data);
      setIndexedDatasets(datasetsRes.data || []);
      setTrainingHistory(historyRes.data || []);
      setVerticals(verticalsRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = () => {
    if (documents.length >= MAX_DOCUMENTS) {
      toast.error(`Maximum ${MAX_DOCUMENTS} documents allowed.`);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size must be less than 50MB');
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      await uploadAIDocument(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      toast.success('Document uploaded successfully');
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload document';
      toast.error(errorMsg);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDoc = async (docId, filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;

    try {
      await deleteAIDocument(docId);
      toast.success('Document deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleAddVideo = async () => {
    if (!videoForm.title || !videoForm.youtube_url) {
      toast.error('Title and YouTube URL are required');
      return;
    }

    try {
      await createAIConsultantVideo(videoForm);
      toast.success('Video added successfully');
      setShowVideoModal(false);
      setVideoForm({ title: '', description: '', youtube_url: '', category: 'general' });
      loadData();
    } catch (error) {
      toast.error('Failed to add video');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;

    try {
      await deleteAIConsultantVideo(videoId);
      toast.success('Video deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleReindex = async () => {
    if (!window.confirm('This will re-index all datasets from documents, videos, and verticals. Continue?')) return;
    
    setIsReindexing(true);
    try {
      const response = await reindexAIConsultant();
      if (response.data.success) {
        toast.success(`Reindex complete: ${response.data.datasets_created} datasets, ${response.data.total_items} items`);
        loadData();
      } else {
        toast.error(response.data.message || 'Reindex failed');
      }
    } catch (error) {
      toast.error('Failed to reindex datasets');
    } finally {
      setIsReindexing(false);
    }
  };

  const handleTrain = async () => {
    if (!window.confirm('This will test the AI with 108 questions and auto-improve responses. This may take a few minutes. Continue?')) return;
    
    setIsTraining(true);
    setTrainingResults(null);
    try {
      const response = await trainAIConsultant();
      if (response.data.success) {
        setTrainingResults(response.data);
        toast.success(`Training complete: ${response.data.passed} passed, ${response.data.needs_improvement} need improvement`);
        loadData();
      } else {
        toast.error(response.data.message || 'Training failed');
      }
    } catch (error) {
      toast.error('Failed to train AI');
    } finally {
      setIsTraining(false);
    }
  };

  const handleTestMemory = async () => {
    if (!window.confirm('This will test the chat memory/session functionality. Continue?')) return;
    
    setIsTestingMemory(true);
    setMemoryTestResults(null);
    try {
      const response = await testChatMemory();
      setMemoryTestResults(response.data);
      if (response.data.success) {
        toast.success(`Memory test passed: ${response.data.passed}/${response.data.total_tests} tests`);
      } else {
        toast.warning(`Memory test: ${response.data.passed}/${response.data.total_tests} tests passed`);
      }
    } catch (error) {
      toast.error('Failed to test chat memory');
    } finally {
      setIsTestingMemory(false);
    }
  };

  const handleDeleteIndexedDataset = async (datasetId) => {
    if (!window.confirm('Delete this indexed dataset?')) return;
    
    try {
      await deleteIndexedDataset(datasetId);
      toast.success('Dataset deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete dataset');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDatasetTypeIcon = (type) => {
    switch (type) {
      case 'document': return <FileText className="w-4 h-4 text-red-500" />;
      case 'video': return <Video className="w-4 h-4 text-purple-500" />;
      case 'vertical_testimonial': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'vertical_faq': return <AlertCircle className="w-4 h-4 text-blue-500" />;
      case 'vertical_deliverable': return <Database className="w-4 h-4 text-cyan-500" />;
      case 'vertical_roi': return <Zap className="w-4 h-4 text-yellow-500" />;
      case 'json': return <FileText className="w-4 h-4 text-orange-500" />;
      default: return <Building2 className="w-4 h-4 text-slate-500" />;
    }
  };

  const filteredDatasets = selectedVerticalFilter
    ? indexedDatasets.filter(d => d.vertical_id === selectedVerticalFilter)
    : indexedDatasets;

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header with Action Buttons */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">AI Consultant Knowledge Base</h1>
            <p className="text-slate-500 mt-1">
              Upload documents, manage videos, and train the AI agent
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button 
              onClick={handleTestMemory}
              disabled={isTestingMemory}
              variant="outline"
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              data-testid="test-memory-btn"
            >
              {isTestingMemory ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <MessageSquare className="w-4 h-4 mr-2" />
              )}
              {isTestingMemory ? 'Testing...' : 'Test Memory'}
            </Button>
            <Button 
              onClick={handleReindex}
              disabled={isReindexing}
              variant="outline"
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
              data-testid="reindex-btn"
            >
              {isReindexing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {isReindexing ? 'Reindexing...' : 'Reindex Datasets'}
            </Button>
            <Button 
              onClick={handleTrain}
              disabled={isTraining}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              data-testid="train-btn"
            >
              {isTraining ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Brain className="w-4 h-4 mr-2" />
              )}
              {isTraining ? 'Training...' : 'Train the Agent'}
            </Button>
          </div>
        </div>

        {/* Memory Test Results */}
        {memoryTestResults && (
          <Card className={`border-2 ${memoryTestResults.success ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                {memoryTestResults.success ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <h3 className={`font-semibold ${memoryTestResults.success ? 'text-emerald-800' : 'text-amber-800'}`}>
                    Chat Memory Test Results
                  </h3>
                  <p className={`text-sm mt-1 ${memoryTestResults.success ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {memoryTestResults.message}
                  </p>
                  <div className="mt-3 space-y-2">
                    {memoryTestResults.tests?.map((test, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        {test.passed ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                        <span className={test.passed ? 'text-emerald-700' : 'text-red-700'}>
                          {test.test_name}: {test.details}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMemoryTestResults(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Banner */}
        <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Brain className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-800">How AI Consultant Works</h3>
                <p className="text-sm text-blue-700 mt-1">
                  The AI Consultant uses indexed datasets from documents, videos, and vertical data to answer user queries.
                  Use <strong>Reindex</strong> to refresh datasets after making changes, <strong>Train</strong> to test and improve responses, and <strong>Test Memory</strong> to verify chat session persistence.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Training Results */}
        {trainingResults && (
          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-green-800">
                <CheckCircle2 className="w-5 h-5" />
                Training Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{trainingResults.total_questions}</p>
                  <p className="text-xs text-slate-500">Total Questions</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{trainingResults.passed}</p>
                  <p className="text-xs text-slate-500">Passed</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-yellow-600">{trainingResults.needs_improvement}</p>
                  <p className="text-xs text-slate-500">Need Improvement</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{trainingResults.failed}</p>
                  <p className="text-xs text-slate-500">Failed</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setTrainingResults(null)}>
                Dismiss
              </Button>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="documents" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-2xl">
            <TabsTrigger value="documents" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="videos" className="flex items-center gap-2">
              <Video className="h-4 w-4" />
              Videos
            </TabsTrigger>
            <TabsTrigger value="indexed" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Indexed Data
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Training History
            </TabsTrigger>
          </TabsList>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">Knowledge Documents</h2>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-500">{documents.length} / {MAX_DOCUMENTS}</span>
                <Button 
                  onClick={handleFileSelect}
                  disabled={isUploading || documents.length >= MAX_DOCUMENTS}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                  data-testid="upload-document-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload PDF
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </div>

            {isUploading && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    <div className="flex-1">
                      <p className="font-medium text-blue-800">Processing document...</p>
                      <Progress value={uploadProgress} className="mt-2" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : documents.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-semibold text-slate-700">No documents yet</h3>
                  <p className="text-slate-500 text-sm mt-1">
                    Upload PDF documents to power the AI Consultant
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {documents.map((doc) => (
                  <Card key={doc.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-medium text-slate-800">{doc.original_filename}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-slate-500">{formatDate(doc.upload_date)}</span>
                              <span className="flex items-center gap-1 text-xs text-green-600">
                                <CheckCircle className="w-3 h-3" /> Indexed
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteDoc(doc.id, doc.original_filename)}
                          className="hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">Reference Videos</h2>
              <Button 
                onClick={() => setShowVideoModal(true)}
                variant="outline"
                className="border-blue-200 text-blue-700 hover:bg-blue-50"
                data-testid="add-consultant-video-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Video URL
              </Button>
            </div>

            {videos.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <Video className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No reference videos added yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {videos.map((video) => (
                  <Card key={video.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Play className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-slate-800">{video.title}</h3>
                            {video.description && (
                              <p className="text-xs text-slate-500 mt-1">{video.description}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteVideo(video.id)}
                          className="hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Indexed Data Tab */}
          <TabsContent value="indexed" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h2 className="text-lg font-semibold text-slate-800">
                Indexed Datasets ({filteredDatasets.length})
              </h2>
              <div className="flex items-center gap-3">
                <select
                  value={selectedVerticalFilter}
                  onChange={(e) => setSelectedVerticalFilter(e.target.value)}
                  className="h-9 px-3 border border-slate-200 rounded-md text-sm"
                >
                  <option value="">All Verticals</option>
                  {verticals.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <Card className="border-slate-200">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Vertical</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                        </TableCell>
                      </TableRow>
                    ) : filteredDatasets.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No indexed datasets. Click &quot;Reindex Datasets&quot; to generate.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDatasets.map((dataset) => (
                        <TableRow key={dataset.dataset_id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getDatasetTypeIcon(dataset.dataset_type)}
                              <span className="text-xs px-2 py-1 bg-slate-100 rounded-full">
                                {dataset.dataset_type?.replace('vertical_', '').replace('_', ' ')}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{dataset.source}</TableCell>
                          <TableCell>
                            {dataset.vertical_name ? (
                              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                                {dataset.vertical_name}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>{dataset.item_count}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteIndexedDataset(dataset.dataset_id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Training History Tab */}
          <TabsContent value="history" className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-800">Training History</h2>
            
            {trainingHistory.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <History className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No training sessions yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {trainingHistory.map((session, idx) => (
                  <Card key={session.id || idx} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-800">
                            Training Session - {formatDate(session.timestamp)}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs flex items-center gap-1">
                              <span className="w-2 h-2 bg-slate-400 rounded-full"></span>
                              {session.total_questions} Questions
                            </span>
                            <span className="text-xs flex items-center gap-1 text-green-600">
                              <CheckCircle2 className="w-3 h-3" />
                              {session.passed} Passed
                            </span>
                            <span className="text-xs flex items-center gap-1 text-yellow-600">
                              <AlertCircle className="w-3 h-3" />
                              {session.needs_improvement} Need Work
                            </span>
                            <span className="text-xs flex items-center gap-1 text-red-600">
                              <XCircle className="w-3 h-3" />
                              {session.failed} Failed
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-slate-900">
                            {Math.round((session.passed / session.total_questions) * 100)}%
                          </p>
                          <p className="text-xs text-slate-500">Pass Rate</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Add Video Modal */}
        {showVideoModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle>Add Reference Video</CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => setShowVideoModal(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Title *</label>
                  <Input
                    placeholder="e.g., ERP Implementation Guide"
                    value={videoForm.title}
                    onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">YouTube URL *</label>
                  <Input
                    placeholder="https://youtube.com/watch?v=..."
                    value={videoForm.youtube_url}
                    onChange={(e) => setVideoForm({ ...videoForm, youtube_url: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Description</label>
                  <Input
                    placeholder="Brief description..."
                    value={videoForm.description}
                    onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowVideoModal(false)}>Cancel</Button>
                  <Button 
                    onClick={handleAddVideo}
                    className="bg-gradient-to-r from-blue-600 to-cyan-600"
                  >
                    Add Video
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Storage Info */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">Storage Usage</p>
                <p className="text-xs text-slate-500">{documents.length} of {MAX_DOCUMENTS} document slots</p>
              </div>
              <Progress value={(documents.length / MAX_DOCUMENTS) * 100} className="w-32" />
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
